import React, { FC, Fragment, useState } from 'react';
import { Button, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router';
import { useFormikContext } from 'formik';
import classNames from 'classnames';
import { get } from 'lodash';

import { useGroupData } from 'hooks';
import { Alert, Loaders } from 'components';

import { getGroupHexColor } from '../../helpers';
import { IGroupForm } from '../../types';

interface IProps {
  isWorking: boolean;
}

const useStyles = makeStyles(theme => ({
  button: {
    borderRadius: 4,
  },
  createButton: {
    marginLeft: theme.spacing(2),
    transition: 'opacity 150ms ease-in-out',
    borderColor: 'transparent',
    color: theme.palette.common.white,
    '&:hover': {
      opacity: 0.9,
      borderColor: 'transparent',
    },
  },
}));

const CreateActions: FC<IProps> = ({ isWorking }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { groupColors } = useGroupData();
  const { values, isValid, dirty } = useFormikContext<IGroupForm>();

  const [isCancelOpen, setIsCancelOpen] = useState(false);

  const isCreateButtonValid = get(values, 'title');
  const hexColor = getGroupHexColor(groupColors, values.color);

  function toggleIsCancel() {
    if (!dirty) {
      return onClickCancel();
    }
    setIsCancelOpen(!isCancelOpen);
  }

  function onClickCancel() {
    history.goBack();
  }

  function renderButtons() {
    return (
      <Fragment>
        <Grid item>
          <Button
            onClick={toggleIsCancel}
            size="large"
            className={classes.button}
            variant="outlined">
            {t('common:cancel')}
          </Button>

          <Alert
            open={isCancelOpen}
            onConfirm={onClickCancel}
            onCancel={toggleIsCancel}
            message={t('groups:cancelMessage')}
            confirm={t('groups:discardEdit')}
          />
        </Grid>

        <Grid item>
          <Button
            disabled={!isValid || !isCreateButtonValid}
            type="submit"
            size="large"
            style={isCreateButtonValid ? { backgroundColor: hexColor } : null}
            className={classNames(classes.button, classes.createButton)}
            variant="contained">
            {t('common:create')}
          </Button>
        </Grid>
      </Fragment>
    );
  }

  function renderLoader() {
    return (
      <Grid item>
        <Loaders.Standard height={40} />
      </Grid>
    );
  }

  return (
    <Grid container justify="flex-end" alignItems="center">
      {isWorking ? renderLoader() : renderButtons()}
    </Grid>
  );
};

export default CreateActions;
